
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, doc, getDoc, where, documentId } from 'firebase/firestore';
import logger from '../utils/logger';
import { jsonCacheService } from './jsonCacheService';
import { USE_CONTRACTS_V2, v2ExactRecords, v2SearchRecords, v2RecordsByIds } from './contractsV2';
import { ejarOpenClose } from './ejarOpenClose';
import { checksForContracts, checksForPlates, checksInWindow } from './storeData';

let archiveCache = null;
let archiveState = null;        // { lastChunkIndex, totalRecords } of the rows in archiveCache
let archiveCheckedAt = 0;       // when _metadata was last compared with them
let archiveLoading = null;      // one refresh at a time for every caller

const ARCHIVE_COLLECTION = 'archive_chunks';
const METADATA_DOC = '_metadata';

// The engine appends new E-Check chunks every sync; the saved copy is compared with _metadata (one read)
// at most this often and only the NEW chunks are read. It used to be kept whole for 24 h, so car
// switches reached Airport Branches / the customer profile / History Lens up to a day late (CON-18).
const ARCHIVE_FRESH_MS = 10 * 60000;
const STATE_FILE = 'archive-state.json';

const chunkIdOf = (index) => `chunk_${index.toString().padStart(5, '0')}`;

/**
 * Parses an E-Check "Transaction Date" cell: an Excel serial number or a
 * "DD/MM/YYYY HH:MM[:SS] [AM|PM]" string. Returns epoch 0 when unreadable.
 */
export const parseArchiveDate = (dateStr) => {
    if (!dateStr) return new Date(0);

    let d;
    if (typeof dateStr === 'number') {
        const excelEpoch = new Date(Date.UTC(1899, 11, 30));
        d = new Date(excelEpoch.getTime() + dateStr * 86400000);
        const timezoneOffsetMS = d.getTimezoneOffset() * 60000;
        d = new Date(d.getTime() + timezoneOffsetMS);
    } else {
        const str = String(dateStr).trim();
        const parts = str.split(/\s+/);
        if (parts.length < 1) return new Date(0);

        const datePart = parts[0];
        const timePart = parts[1] || '00:00:00';
        const ampm = parts[2] ? parts[2].toUpperCase() : null;

        const [day, month, year] = datePart.split(/[-/]/).map(Number);
        let [hours, minutes, seconds] = timePart.split(':').map(Number);

        if (isNaN(hours)) hours = 0;
        if (isNaN(minutes)) minutes = 0;
        if (isNaN(seconds)) seconds = 0;

        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;

        d = new Date(year, month - 1, day, hours, minutes, seconds);
    }
    return isNaN(d.getTime()) ? new Date(0) : d;
};

/**
 * How to bring a saved copy up to date with _metadata:
 *   'none'   - it is current (same last chunk and record count), or there is no _metadata to compare;
 *   'append' - only chunks after `after` up to `upTo` are new, holding `expected` records;
 *   'full'   - no copy / no bookkeeping, or anything else (a full re-import rewrote the chunks).
 */
export const planArchiveRefresh = (state, md, haveRows) => {
    if (!haveRows) return { action: 'full' };
    if (!md) return { action: 'none' };
    if (!state || state.lastChunkIndex == null || state.totalRecords == null) return { action: 'full' };
    const last = md.last_chunk_index ?? -1;
    const total = md.total_records ?? 0;
    if (last === state.lastChunkIndex && total === state.totalRecords) return { action: 'none' };
    if (last > state.lastChunkIndex && total > state.totalRecords) {
        return { action: 'append', after: state.lastChunkIndex, upTo: last, expected: total - state.totalRecords };
    }
    return { action: 'full' };
};

/** What was typed in an archive search: 'contract' (I2609000D01443839), 'plate' (W 28656, 28656) or 'text'
 *  (a customer name, a phone, a booking number). */
export const archiveTermKind = (term) => {
    const t = String(term ?? '').trim();
    const compact = t.replace(/\s+/g, '');
    if (compact.length >= 8 && /^[A-Za-z]{1,2}\d{5,}[A-Za-z0-9]*$/.test(compact)) return 'contract';
    if (/^[A-Za-z]{0,3}[\s-]*\d{1,6}$/.test(t)) return 'plate';
    return 'text';
};

const STORE_SEARCH_CONTRACTS = 30;     // a name that matches hundreds of contracts: the newest 30 (newest pick-up first)

/**
 * The E-Check rows of an archive search from the store (USE_CONTRACTS_V2): a contract number -> its checks; a plate
 * -> the car's checks; anything else (and a number or plate with no checks) -> the contracts the store finds for it
 * (name, phone, identity, booking, plate) -> their checks. Branch / model / check-type words are not searched there.
 */
const storeSearch = async (term) => {
    const t = String(term ?? '').trim();
    if (!t) return [];
    const kind = archiveTermKind(t);
    let rows = [];
    if (kind === 'contract') rows = await checksForContracts([t.replace(/\s+/g, '')]);
    else if (kind === 'plate') rows = await checksForPlates([t]);
    if (rows.length) return rows;
    const contracts = (await v2SearchRecords(t, 'all')).map((v) => v.contract_no).filter(Boolean).slice(0, STORE_SEARCH_CONTRACTS);
    return contracts.length ? checksForContracts(contracts) : [];
};

export const archiveService = {
    /**
     * The whole E-Check archive (rows of every chunk). Kept in memory and in the saved copy; refreshed
     * by reading only the chunks added since (see planArchiveRefresh). A failed read throws the real
     * error when there is no copy at all - the old fallback to /archive_data.json (an empty file) only
     * turned it into a JSON parse error.
     */
    async load() {
        if (archiveCache && Date.now() - archiveCheckedAt < ARCHIVE_FRESH_MS) return archiveCache;
        if (!archiveLoading) archiveLoading = this._refresh().finally(() => { archiveLoading = null; });
        return archiveLoading;
    },

    async _refresh() {
        let rows = archiveCache;
        let state = archiveState;
        if (!rows) {
            const [saved, savedState] = await Promise.all([jsonCacheService.loadArchive(), jsonCacheService.loadFromFile(STATE_FILE)]);
            if (Array.isArray(saved) && saved.length) { rows = saved; state = savedState ? savedState.data : null; }
        }

        let md;
        try {
            md = await this.getMetadata();
        } catch (error) {
            if (rows) {   // offline / refused: the saved copy, checked again on the next load
                logger.warn('[archive] _metadata not read, the saved copy is used:', error);
                archiveCache = rows; archiveState = state; archiveCheckedAt = Date.now();
                return rows;
            }
            throw error;
        }

        const plan = planArchiveRefresh(state, md, !!rows);
        if (plan.action === 'none') {
            // (no _metadata at all: the saved copy while younger than 24 h, as before)
            if (md || await jsonCacheService.isCacheValid('archive.json', 24)) {
                archiveCache = rows; archiveState = state; archiveCheckedAt = Date.now();
                return rows;
            }
        }

        const col = collection(db, ARCHIVE_COLLECTION);
        if (plan.action === 'append') {
            const snap = await getDocs(query(col, where(documentId(), '>', chunkIdOf(plan.after)), where(documentId(), '<=', chunkIdOf(plan.upTo))));
            let added = [];
            snap.forEach((d) => { const c = d.data(); if (c && Array.isArray(c.data)) added = added.concat(c.data); });
            if (added.length === plan.expected) {
                const next = rows.concat(added);
                await this._keep(next, md);
                logger.info(`[archive] +${added.length} records from ${snap.size} new chunk(s)`);
                return next;
            }
            logger.warn(`[archive] new chunks hold ${added.length} records, _metadata says ${plan.expected} - reading it whole`);
        }

        // The whole archive (first load, a full re-import, or counts that do not add up)
        const snapshot = await getDocs(query(col, orderBy('__name__')));
        let data = [];
        snapshot.forEach((d) => { const c = d.data(); if (c && Array.isArray(c.data)) data = data.concat(c.data); });
        if (data.length === 0) throw new Error('The E-Check archive is empty (archive_chunks has no records).');
        await this._keep(data, md);
        return data;
    },

    async _keep(rows, md) {
        archiveCache = rows;
        archiveState = md ? { lastChunkIndex: md.last_chunk_index ?? -1, totalRecords: md.total_records ?? rows.length } : null;
        archiveCheckedAt = Date.now();
        await jsonCacheService.saveArchive(rows);
        await jsonCacheService.saveToFile(STATE_FILE, archiveState);
    },

    /**
    * Searches the archive based on criteria.
    * @param {Object} criteria - { search: string, date: string }
    */
    async search({ search, date }) {
        if (USE_CONTRACTS_V2) {
            const searchDate = date ? date.trim() : '';
            if (!String(search || '').trim()) return [];     // a date alone: the caller reads that day's window
            const rows = await storeSearch(search);
            return searchDate ? rows.filter((r) => String(r['Transaction Date'] || '').includes(searchDate)) : rows;
        }
        const data = await this.load();

        const lowerSearch = search ? search.toLowerCase().trim() : "";
        const searchDate = date ? date.trim() : "";

        // logger.log(` Searching Archive: "${lowerSearch}" Date: "${searchDate}"`);

        if (!lowerSearch && !searchDate) return [];

        // Helper to normalize strings for comparison (remove spaces for plates)
        const normalize = (str) => str ? str.toString().toLowerCase().replace(/\s+/g, '') : "";
        const lowerSearchNormalized = normalize(search);

        return data.filter(item => {
            let match = true;

            // Search Text (Contract No, Plate, Branch, Check Type, Model, Customer, Booking, Phone)
            if (lowerSearch) {
                const textMatch =
                    (item['Contract No.'] && item['Contract No.'].toLowerCase().includes(lowerSearch)) ||
                    (item['Plate Number'] && normalize(item['Plate Number']).includes(lowerSearchNormalized)) ||
                    (item['Branch'] && item['Branch'].toLowerCase().includes(lowerSearch)) ||
                    (item['Check Type'] && item['Check Type'].toLowerCase().includes(lowerSearch)) ||
                    (item['Has Damages'] && item['Has Damages'].toLowerCase().includes(lowerSearch)) ||
                    (item['Model'] && item['Model'].toLowerCase().includes(lowerSearch)) ||
                    // Expanded Fields
                    (item['Customer Name'] && item['Customer Name'].toLowerCase().includes(lowerSearch)) ||
                    (item['Customer'] && item['Customer'].toLowerCase().includes(lowerSearch)) ||
                    (item['Booking Number'] && item['Booking Number'].toString().toLowerCase().includes(lowerSearch)) ||
                    (item['Booking No'] && item['Booking No'].toString().toLowerCase().includes(lowerSearch)) ||
                    (item['Phone Number'] && item['Phone Number'].toString().includes(lowerSearch));

                if (!textMatch) match = false;
            }

            // Search Date (Exact Match on Transaction Date)
            // Assuming Transaction Date format is DD/MM/YYYY HH:MM
            if (searchDate && match) {
                const dateMatch =
                    (item['Transaction Date'] && item['Transaction Date'].includes(searchDate));
                if (!dateMatch) match = false;
            }

            return match;
        });
    },

    /**
     * Finds archive records by a list of Contract Numbers.
     * @param {string[]} contractNumbers
     */
    async findByContractNumbers(contractNumbers) {
        if (!contractNumbers || contractNumbers.length === 0) return [];
        if (USE_CONTRACTS_V2) return checksForContracts(contractNumbers.map((c) => String(c ?? '').trim()));
        const data = await this.load();

        // Normalize for comparison
        const searchSet = new Set(contractNumbers.map(c => c.toString().trim().toLowerCase()));

        return data.filter(item => {
            const cNo = item['Contract No.'] || item['Contract No'] || item['Agreement'];
            return cNo && searchSet.has(cNo.toString().trim().toLowerCase());
        });
    },

    /**
     * History Lens' search (Archive Search, Contract Timeline): the E-Checks of every contract the term names - whole
     * lives - and the contracts the store holds for it with no E-Check yet, each contract opened and closed by the
     * owner's rule (ejarOpenClose: open = Ejar first, close = whichever is first; owner, 6 Oct 2026 - CC 32549 opened
     * on Ejar 05/10 with no E-Check did not show). A plate typed as digits ("32549") is the car's, never every phone /
     * contract number holding those digits. -> rows (in no order - the pages sort them); without the contracts store
     * (or Ejar's records not read): the E-Checks as before.
     */
    async historySearch(term) {
        const t = String(term ?? '').trim();
        if (!t) return [];
        let rows = await this.search({ search: t });
        if (!USE_CONTRACTS_V2) {
            const nos = [...new Set(rows.map((r) => r['Contract No.']).filter(Boolean))];
            return nos.length ? this.findByContractNumbers(nos) : rows;
        }
        // the store's contracts of the term: not read -> the E-Checks as before (review: a search never fails for it)
        let found = await v2SearchRecords(t, 'all').catch(() => []);
        const kind = archiveTermKind(t);
        if (kind === 'plate') {
            const typed = t.toUpperCase().replace(/[^A-Z0-9]/g, '');
            const digitsOnly = /^\d+$/.test(typed);
            const key = (p) => String(p || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
            const isCar = (p) => !!p && (digitsOnly ? p.replace(/^[A-Z]+/, '') === typed : p === typed);
            const cars = found.filter((v) => [v.car?.plate, v.first_car?.plate, v.invygo_plate].map(key).some(isCar));
            if (cars.length) {
                // the car's contracts: the E-Checks of the car are kept, the look-alikes (a phone or a contract
                // number holding the digits) are not
                const carNos = new Set(cars.map((v) => String(v.contract_no || '').toUpperCase()));
                const byPlate = rows.filter((r) => isCar(key(r['Plate Number'])));
                rows = byPlate.length ? byPlate : rows.filter((r) => carNos.has(String(r['Contract No.'] || '').toUpperCase()));
                found = cars;
            } else if (rows.length) {
                found = [];          // the car's E-Checks found, no contract names it: never the look-alikes (review)
            }
        } else if (kind === 'contract') {
            const want = t.replace(/\s+/g, '').toUpperCase();
            found = found.filter((v) => [v.contract_no, v.booking_number].some((x) => String(x || '').toUpperCase() === want));
        }
        found = found.slice(0, STORE_SEARCH_CONTRACTS);
        const nos = [...new Set([...rows.map((r) => r['Contract No.']), ...found.map((v) => v.contract_no)]
            .map((c) => String(c || '').trim().toUpperCase()).filter(Boolean))];
        if (!nos.length) return rows;
        const [whole, records] = await Promise.all([checksForContracts(nos), v2RecordsByIds(nos, 'all').catch(() => [])]);
        return ejarOpenClose([...whole, ...rows.filter((r) => !r['Contract No.'])], records);
    },

    /**
     * Finds archive records by Booking Number.
     * @param {string|number} bookingNumber
     */
    async findByBookingNumber(bookingNumber) {
        if (!bookingNumber) return [];
        if (USE_CONTRACTS_V2) {
            // an E-Check carries no booking number: the store's contracts of that exact booking, then their checks
            const booking = bookingNumber.toString().trim();
            const contracts = (await v2ExactRecords(booking, 'all'))
                .filter((v) => String(v.booking_number || '').trim().toUpperCase() === booking.toUpperCase())
                .map((v) => v.contract_no);
            if (!contracts.length) return [];
            return (await checksForContracts(contracts)).map((r) => ({ ...r, 'Booking Number': booking }));
        }
        const data = await this.load();

        const searchVal = bookingNumber.toString().trim();

        return data.filter(item => {
            // Check all known variations
            const bNo = item['Booking Number'] ||
                item['Booking No'] ||
                item['Booking No.'] ||
                item['Booking ID'] ||
                item['Booking_ID'] ||
                item['ID']; // Sometimes users map ID to Booking ID

            return bNo && bNo.toString().trim() === searchVal;
        });
    },

    /**
    * Clears the cache (useful if we implement a reload feature)
    */
    /**
     * The E-Check rows between two instants (ms), oldest first - the store's checks a week a request (only with
     * USE_CONTRACTS_V2; the Firestore archive is read whole and cut to the window). For the pages that read a
     * period of the archive instead of all of it.
     */
    async loadWindow(fromMs, toMs, { onProgress } = {}) {
        if (USE_CONTRACTS_V2) return checksInWindow(fromMs, toMs, { onProgress });
        const rows = await this.load();
        return rows.filter((r) => {
            const t = parseArchiveDate(r['Transaction Date']).getTime();
            return t >= fromMs && t <= toMs;
        });
    },

    clearCache() {
        archiveCache = null;
        archiveState = null;
        archiveCheckedAt = 0;
    },

    // The E-Check archive is written by the engine's hourly Ejar sync only (merge_echeck); its upload is gone
    // (owner, 28 Sep 2026) - a hand "Full refresh" could also move the watermark the engine relies on.

    /** The `_metadata` doc (last_chunk_index, total_records, latest_record_timestamp, last_sync_time) or null. */
    async getMetadata() {
        const snap = await getDoc(doc(db, ARCHIVE_COLLECTION, METADATA_DOC));
        return snap.exists() ? snap.data() : null;
    }
};
